import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Door 1 (A3, design §11.3): the public walk-in lead form. Deliberately
// minimal — identity, reachability, matter category, adverse party name
// (captured FOR the conflict check), a two-sentence narrative. No SSN, no
// fee, no scheduling promise, no legal questions. The pipeline's conflict
// check runs before anything more is solicited.
function PublicWalkInForm() {
  const [firmName, setFirmName] = useState('');
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    matter_category: '', adverse_party_name: '', narrative: '', referral: '',
    website: '', // honeypot — hidden from humans
  });

  useEffect(() => {
    fetch('/api/public/firm-info')
      .then(r => r.json())
      .then(d => setFirmName(d.firm_name || ''))
      .catch(() => {});
    axios.get(`${API_URL}/intake/walk-in/categories`)
      .then(r => setCategories(r.data?.categories || []))
      .catch(() => setCategories([]));
  }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.email.trim() && !form.phone.trim()) {
      setError('Please provide an email address or phone number so we can reach you.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/intake/walk-in`, {
        ...form,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        matter_category: form.matter_category || null,
        adverse_party_name: form.adverse_party_name.trim() || null,
        narrative: form.narrative.trim() || null,
        referral: form.referral.trim() || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again or call our office.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-xl p-8 max-w-lg w-full text-center">
          <h1 className="text-2xl font-bold text-white mb-3">Thank You</h1>
          <p className="text-slate-300">
            Your information has been received. We will review it — including a
            check for any conflicts of interest — and be in touch.
          </p>
          <p className="text-slate-400 text-sm mt-4">
            Submitting this form does not create an attorney-client relationship.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 py-10 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white">{firmName || 'Our Firm'}</h1>
          <p className="text-slate-400 mt-2">Tell us briefly about your situation and we'll be in touch.</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-slate-800 rounded-xl p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">First Name *</label>
              <input required maxLength={100} value={form.first_name} onChange={set('first_name')}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Last Name *</label>
              <input required maxLength={100} value={form.last_name} onChange={set('last_name')}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">Email</label>
            <input type="email" value={form.email} onChange={set('email')}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">Phone</label>
            <input maxLength={30} value={form.phone} onChange={set('phone')}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">What kind of matter is this?</label>
            <select value={form.matter_category} onChange={set('matter_category')}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
              <option value="">Select...</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Who is on the other side? <span className="text-slate-500">(person or company, if any)</span>
            </label>
            <input maxLength={300} value={form.adverse_party_name} onChange={set('adverse_party_name')}
              placeholder="e.g. the other parent, opposing company"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
            <p className="text-xs text-slate-500 mt-1">We use this to check for conflicts of interest before going further.</p>
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">Briefly, what happened? <span className="text-slate-500">(a sentence or two)</span></label>
            <textarea maxLength={600} rows={3} value={form.narrative} onChange={set('narrative')}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">How did you hear about us?</label>
            <input maxLength={200} value={form.referral} onChange={set('referral')}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
          </div>
          {/* Honeypot — hidden from humans, bots fill it */}
          <input type="text" value={form.website} onChange={set('website')} autoComplete="off"
            tabIndex={-1} aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }} />
          {error && (
            <div className="bg-red-900/40 border border-red-600/50 rounded-lg p-3 text-red-300 text-sm">{error}</div>
          )}
          <button type="submit" disabled={submitting}
            className="w-full py-3 bg-teal-600 hover:bg-teal-500 disabled:opacity-60 rounded-lg text-white font-semibold">
            {submitting ? 'Sending…' : 'Send'}
          </button>
          <p className="text-xs text-slate-500 text-center">
            Please don't include confidential details yet. Submitting this form
            does not create an attorney-client relationship.
          </p>
        </form>
      </div>
    </div>
  );
}

export default PublicWalkInForm;
